import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Home, ArrowLeft, NavigationOff } from 'lucide-react';
import { SiteFooter } from '@/components/common/SiteFooter';
import { BRAND_NAME } from '@/utils/branding';

// Random trace ID per visit, e.g. ORG-3F72B1-C4
const generateTraceId = (): string => {
  const hex = (len: number) =>
    Array.from({ length: len }, () => '0123456789ABCDEF'[Math.floor(Math.random() * 16)]).join('');
  return `ORG-${hex(6)}-${hex(2)}`;
};

export const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();

  const traceId = useMemo(() => generateTraceId(), []);

  // Decorative metadata chips (visual spec from the approved design).
  const chips: { label: string; value: string }[] = [
    { label: 'Status Code', value: '404 - NOT_FOUND' },
    { label: 'Trace ID', value: traceId },
    { label: 'Protocol', value: `${BRAND_NAME}-HTTP/2.4` },
  ];

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  return (
    <div className="dark min-h-screen flex flex-col bg-background text-foreground">
      <main className="relative flex-1 flex items-center justify-center px-6 py-16 overflow-hidden">
        {/* decorative floating panels */}
        <div
          className="pointer-events-none absolute top-16 right-[8%] w-44 h-44 rounded-2xl border border-border/60"
          aria-hidden="true"
        />

        <div className="relative max-w-2xl w-full text-center">
          {/* ghost 404 watermark with icon on top */}
          <div className="relative flex items-center justify-center h-44 sm:h-56 mb-2">
            <span
              className="absolute inset-0 flex items-center justify-center text-[140px] sm:text-[200px] font-bold leading-none tracking-tight text-foreground/[0.06] select-none"
              aria-hidden="true"
            >
              404
            </span>
            <span className="relative w-16 h-16 rounded-full border-2 border-foreground/80 flex items-center justify-center">
              <NavigationOff className="w-7 h-7 text-foreground/80" />
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-5">
            Lost in the architecture.
          </h1>
          <p className="text-muted-foreground text-[17px] leading-relaxed max-w-xl mx-auto mb-10">
            The intelligence you're searching for is currently inaccessible. It may
            have been relocated or purged from the primary node.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-12">
            <Button onClick={handleGoHome} className="h-12 px-7 gap-2">
              <Home className="w-4 h-4" />
              Back to Dashboard
            </Button>
            <Button onClick={handleGoBack} variant="outline" className="h-12 px-7 gap-2">
              <ArrowLeft className="w-4 h-4" />
              Previous State
            </Button>
          </div>

          {/* decorative metadata chips */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl mx-auto">
            {chips.map((chip) => (
              <div
                key={chip.label}
                className="rounded-lg border border-border bg-card/50 px-4 py-3 text-left"
              >
                <div className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
                  {chip.label}
                </div>
                <div className="mt-1 text-sm text-foreground/90">{chip.value}</div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default NotFoundPage;
